import { Edit3, RefreshCw, Search, Shield } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

export const PayrollAttendanceOperationsSection = ({
  attendanceLoading,
  attendanceReport,
  attendanceRecords,
  attendanceClosures,
  activeAttendanceFiltersCount,
  attendanceSearchQuery,
  setAttendanceSearchQuery,
  attendanceSortField,
  setAttendanceSortField,
  attendanceSortDirection,
  setAttendanceSortDirection,
  attendanceSortOptions,
  attendanceBootstrapStatus,
  attendanceResetIncludesShifts,
  setAttendanceResetIncludesShifts,
  loadAttendanceData,
  handleResetAttendanceOperationalData,
  submitting,
  manualAttendanceForm,
  setManualAttendanceForm,
  sortedEmployees,
  handleSaveManualAttendance,
}) => (
  <>
    {attendanceLoading && (
      <Card className="border-dashed" data-testid="attendance-loading-state">
        <CardContent className="flex items-center gap-3 py-6 text-sm text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          A carregar blocos de assiduidade do período seleccionado...
        </CardContent>
      </Card>
    )}

    <Card className="overflow-hidden rounded-xl border-slate-200 shadow-sm" data-testid="payroll-attendance-toolbar-card">
      <CardContent className="space-y-2 p-3">
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="payroll-attendance-kpi-strip">
          <Badge variant="outline" className="rounded-full bg-white">Registos: {attendanceReport.summary?.total_records || attendanceRecords.length || 0}</Badge>
          <Badge variant="outline" className="rounded-full bg-white">Horas: {attendanceReport.summary?.total_hours || 0}</Badge>
          <Badge variant="outline" className="rounded-full bg-white">Fechos: {attendanceClosures.length}</Badge>
          <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700">Faltas: {attendanceReport.summary?.unjustified_absences || 0}</Badge>
          {activeAttendanceFiltersCount > 0 && <Badge variant="outline" className="rounded-full bg-slate-100">{activeAttendanceFiltersCount} filtro(s)</Badge>}
        </div>

        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white/90 p-2 shadow-sm" data-testid="payroll-attendance-controls">
          <div className="relative min-w-[220px] flex-[1.2]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Pesquisar funcionário, loja, data ou estado..."
              value={attendanceSearchQuery}
              onChange={(event) => setAttendanceSearchQuery(event.target.value)}
              className="h-8 rounded-lg border-slate-200 bg-slate-50 pl-9 text-sm"
              data-testid="payroll-attendance-search-input"
            />
          </div>

          <Select value={attendanceSortField} onValueChange={setAttendanceSortField}>
            <SelectTrigger className="h-8 w-[156px] rounded-lg border-slate-200 bg-slate-50 text-sm" data-testid="payroll-attendance-sort-field">
              <SelectValue placeholder="Ordenar" />
            </SelectTrigger>
            <SelectContent>
              {attendanceSortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={attendanceSortDirection} onValueChange={setAttendanceSortDirection}>
            <SelectTrigger className="h-8 w-[96px] rounded-lg border-slate-200 bg-slate-50 text-sm" data-testid="payroll-attendance-sort-direction">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">Asc.</SelectItem>
              <SelectItem value="desc">Desc.</SelectItem>
            </SelectContent>
          </Select>

          {activeAttendanceFiltersCount > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setAttendanceSearchQuery('')} className="h-8 rounded-lg border border-slate-200 bg-slate-50 px-2 text-slate-700 hover:bg-slate-100" data-testid="payroll-attendance-clear-filters-button">
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>

    <div className="grid gap-4 xl:grid-cols-[0.95fr_1.2fr]">
      <Card className="rounded-2xl border-slate-200 shadow-sm" data-testid="attendance-bootstrap-card">
        <CardHeader className="space-y-1 pb-3">
          <CardTitle className="flex items-center gap-2 text-base"><Shield className="h-4 w-4" /> Base limpa e pronta</CardTitle>
          <CardDescription>Estado operacional da assiduidade para arranque real sem dependência de dados fake.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 text-xs" data-testid="attendance-bootstrap-strip">
            <Badge variant="outline" className="rounded-full bg-white">Trabalhadores prontos: {attendanceBootstrapStatus.employees_ready || 0}</Badge>
            <Badge variant="outline" className="rounded-full bg-white">Registos: {attendanceBootstrapStatus.counts?.attendance_records || 0}</Badge>
            <Badge variant="outline" className="rounded-full bg-white">Manuais: {attendanceBootstrapStatus.counts?.manual_records || 0}</Badge>
            <Badge variant="outline" className="rounded-full bg-white">Imports: {attendanceBootstrapStatus.counts?.imported_records || 0}</Badge>
            <Badge variant="outline" className={attendanceBootstrapStatus.is_empty_operational_data ? 'rounded-full bg-green-50 text-green-700' : 'rounded-full bg-amber-50 text-amber-700'}>
              {attendanceBootstrapStatus.is_empty_operational_data ? 'Base operacional vazia' : 'Base com histórico'}
            </Badge>
          </div>
          <p className="text-sm text-slate-600">{attendanceBootstrapStatus.notes || 'Pode começar do zero com lançamento manual ou importação real.'}</p>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="attendance-reset-shifts-row">
            <div>
              <p className="text-sm font-medium text-slate-900">Limpar também os turnos</p>
              <p className="text-xs text-slate-500">Use isto apenas se quiser reiniciar toda a base operacional.</p>
            </div>
            <Switch checked={attendanceResetIncludesShifts} onCheckedChange={setAttendanceResetIncludesShifts} data-testid="attendance-reset-shifts-switch" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={() => loadAttendanceData({ force: true })} data-testid="attendance-bootstrap-refresh-button">
              <RefreshCw className="mr-1 h-4 w-4" />
              Actualizar estado
            </Button>
            <Button variant="destructive" size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={handleResetAttendanceOperationalData} disabled={submitting} data-testid="attendance-reset-operational-data-button">
              Reiniciar base operacional
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200 shadow-sm" data-testid="attendance-manual-entry-card">
        <CardHeader className="space-y-1 pb-3">
          <CardTitle className="flex items-center gap-2 text-base"><Edit3 className="h-4 w-4" /> Lançamento manual</CardTitle>
          <CardDescription>Introduza assiduidade real desde o zero, sem depender de ficheiros de teste.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Trabalhador</Label>
              <Select value={manualAttendanceForm.employee_number} onValueChange={(value) => setManualAttendanceForm((current) => ({ ...current, employee_number: value }))}>
                <SelectTrigger data-testid="attendance-manual-employee-select">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {sortedEmployees.filter((employee) => employee.employee_number).map((employee) => (
                    <SelectItem key={employee.employee_number} value={employee.employee_number}>{employee.employee_number} · {employee.full_name || employee.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Data</Label><Input type="date" value={manualAttendanceForm.date} onChange={(event) => setManualAttendanceForm((current) => ({ ...current, date: event.target.value }))} data-testid="attendance-manual-date-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Entrada</Label><Input type="time" value={manualAttendanceForm.work_start} onChange={(event) => setManualAttendanceForm((current) => ({ ...current, work_start: event.target.value }))} data-testid="attendance-manual-start-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Início almoço</Label><Input type="time" value={manualAttendanceForm.lunch_start} onChange={(event) => setManualAttendanceForm((current) => ({ ...current, lunch_start: event.target.value }))} data-testid="attendance-manual-lunch-start-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Fim almoço</Label><Input type="time" value={manualAttendanceForm.lunch_end} onChange={(event) => setManualAttendanceForm((current) => ({ ...current, lunch_end: event.target.value }))} data-testid="attendance-manual-lunch-end-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Saída</Label><Input type="time" value={manualAttendanceForm.work_end} onChange={(event) => setManualAttendanceForm((current) => ({ ...current, work_end: event.target.value }))} data-testid="attendance-manual-end-input" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Notas</Label><Textarea value={manualAttendanceForm.notes} onChange={(event) => setManualAttendanceForm((current) => ({ ...current, notes: event.target.value }))} rows={2} data-testid="attendance-manual-notes-input" /></div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="h-8 rounded-lg px-2 text-xs" onClick={handleSaveManualAttendance} disabled={submitting} data-testid="attendance-manual-save-button">Guardar lançamento manual</Button>
            <Button size="sm" variant="outline" className="h-8 rounded-lg px-2 text-xs" onClick={() => setManualAttendanceForm({ employee_number: '', date: '', work_start: '08:00', lunch_start: '12:00', lunch_end: '13:00', work_end: '17:00', notes: '' })} data-testid="attendance-manual-reset-button">Limpar</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  </>
);